// history-chart.tsx — COURBE D'HISTORIQUE de latence d'un service (SA-9.1).
//
// Écrit en SVG à la main : aucune bibliothèque de graphes dans le dépôt, la maquette dessine déjà ses
// jauges ainsi, et les jetons `--chart-*` donnent la palette. Une dépendance de 100 ko qu'il faudrait
// ensuite combattre pour la faire entrer dans la charte serait un mauvais échange.
//
// ⚠️ CE QUE CE GRAPHE REFUSE DE FAIRE (§23.0) :
//   · tracer une MOYENNE — elle noie les pics, qui sont la raison même d'ouvrir un historique ;
//   · dessiner un service injoignable à 0 ms — une ligne au plancher se lit « instantané » ;
//   · relier deux points séparés par un TROU de mesure — la ligne affirmerait une continuité qui
//     n'a pas été observée (sonde arrêtée, service redémarré). Le trou est désormais DESSINÉ.
import { useEffect, useMemo, useRef, useState } from 'react';
import type { HealthMeasure, History, HistoryBucket } from './api';
import { latencyZones } from './service-health';

/** Périodes offertes. ⚠️ Bornées par la RÉTENTION (7 jours, appliquée aussi côté serveur) : proposer
 *  davantage rendrait un graphe vide, qui se lit « il ne s'est rien passé » au lieu de « on ne le
 *  garde pas ». Le serveur écrête de son côté — l'écran ne doit pas être la seule garde. */
export const RANGES = [
  { k: '1h', label: '1 h', hours: 1 },
  { k: '6h', label: '6 h', hours: 6 },
  { k: '24h', label: '24 h', hours: 24 },
  { k: '7j', label: '7 j', hours: 168 },
] as const;

// Hauteur du tracé. 190 → 205 (+8 %, demande utilisateur 2026-08-12) : une fois le dessin ramené à
// l'échelle 1:1, la bande paraissait basse. ⚠️ Les marges (PAD_*) ne bougent PAS — c'est la zone de
// tracé qui gagne les 15 px, donc la lecture des latences, pas les libellés.
const H = 205;
const PAD_L = 44;           // place des libellés de l'axe des ms
const PAD_B = 22;           // place des libellés de temps
const PAD_T = 10;


/**
 * ⚠️ 9 % ET NON LES 4,5 % DU HANDOFF (arbitrage 2026-08-12). Cette valeur était calibrée sur le rendu
 * de la maquette ; chez nous les zones se posent sur une carte VITRÉE très sombre, où 4,5 % d'un teal
 * moyen sur du quasi-noir ne déplace que ~8 unités de vert — invisible. À 9 % le repère se voit sans
 * devenir un aplat décoratif. Conséquence voulue : sur un service sain, l'échelle se cale sur 100 ms,
 * la zone verte occupe tout le graphe, et « tout est dans le vert » se lit d'un coup d'œil.
 */
const OPACITE_ZONE = 0.09;

/** Un trou commence au-delà de 1,5 seau d'écart — MÊME seuil que la coupe des segments : les deux
 *  règles doivent bouger ensemble, sinon on dessine un trou là où la ligne reste continue. */
const SEUIL_TROU = 1.5;

/** Plafond de l'axe : le palier au-dessus du pire maximum, pour que l'échelle ait un sens stable. */
function cap(max: number): number {
  for (const p of [100, 300, 500, 700, 1000, 2000, 5000]) if (max <= p) return p;
  return Math.ceil(max / 1000) * 1000;
}

const heure = (t: number) => new Date(t).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
const day = (t: number) => new Date(t).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });

/** Segments continus : on coupe dès qu'un seau manque à l'appel (trou de mesure) ou n'a aucune latence. */
function segments(buckets: HistoryBucket[], bucketMs: number): HistoryBucket[][] {
  const out: HistoryBucket[][] = [];
  let cur: HistoryBucket[] = [];
  let precedent: HistoryBucket | null = null;
  for (const b of buckets) {
    const trou = precedent != null && b.t - precedent.t > bucketMs * SEUIL_TROU;
    if (b.median == null || trou) { if (cur.length) out.push(cur); cur = []; }
    if (b.median != null) cur.push(b);
    precedent = b;
  }
  if (cur.length) out.push(cur);
  return out;
}

/** Intervalles SANS aucun relevé. Exporté : le pied du panneau les compte avec la MÊME règle. */
export function trousDeMesure(buckets: HistoryBucket[], bucketMs: number): { a: HistoryBucket; b: HistoryBucket }[] {
  const out: { a: HistoryBucket; b: HistoryBucket }[] = [];
  for (let i = 1; i < buckets.length; i++) {
    const a = buckets[i - 1]!; const b = buckets[i]!;
    if (b.t - a.t > bucketMs * SEUIL_TROU) out.push({ a, b });
  }
  return out;
}

/**
 * ⚠️ LE SVG EST DESSINÉ À L'ÉCHELLE 1:1, ET C'EST TOUT L'ENJEU DE LA LISIBILITÉ.
 *
 * Un `viewBox` fixe (900 unités) avec `width="100%"` fait grandir TOUT le dessin avec le conteneur :
 * sur un écran 1920, la colonne de contenu fait ~1600 px, donc un facteur 1,8 — et une étiquette
 * écrite à 9,5 px s'affichait à 17 px, énorme à côté du reste de l'application (signalé le 2026-08-12).
 * La hauteur suivait aussi : 190 devenait ~340.
 *
 * On MESURE donc le conteneur et on dessine dans ses pixels réels : une unité SVG = un pixel CSS, les
 * tailles écrites ici sont celles qui s'affichent. `width` reste surchargeable (tests, largeur imposée).
 *
 * ⚠️ `measure` porte les paliers qui colorent les TUILES : les zones de gravité du fond les reprennent, sans quoi un
 *    service orange sur sa tuile serait peint sur fond rouge (ou l'inverse) — l'écran se contredirait sur la même mesure.
 */
export function HistoryChart({ data, measure, width: impose }: { data: History; measure: HealthMeasure; width?: number }) {
  const { buckets, bucketSeconds } = data;
  const boite = useRef<HTMLDivElement>(null);
  const [mesure, setMesure] = useState(0);
  useEffect(() => {
    if (impose) return;
    const el = boite.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setMesure(Math.round(e!.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [impose]);
  const width = impose ?? mesure;
  // ⚠️ Survol ÉPHÉMÈRE : jamais persisté, jamais remonté au parent. Changer de service ou de période
  // l'efface — laisser affichée la lecture d'un seau qui n'est plus à l'écran serait un chiffre faux.
  const [survol, setSurvol] = useState<HistoryBucket | null>(null);
  useEffect(() => { setSurvol(null); }, [data]);

  const vue = useMemo(() => {
    const avec = buckets.filter((b) => b.max != null);
    const maxMs = avec.length ? Math.max(...avec.map((b) => b.max!)) : 100;
    const top = cap(maxMs);
    const t0 = buckets[0]?.t ?? 0;
    const t1 = buckets[buckets.length - 1]?.t ?? t0 + 1;
    const span = Math.max(1, t1 - t0);
    const utile = width - PAD_L - 8;
    const x = (t: number) => PAD_L + ((t - t0) / span) * utile;
    const y = (ms: number) => PAD_T + (1 - Math.min(1, ms / top)) * (H - PAD_T - PAD_B);
    return { top, t0, t1, span, utile, x, y, largeurSeau: Math.max(1, utile / Math.max(1, span / (bucketSeconds * 1000))) };
  }, [buckets, bucketSeconds, width]);

  if (!buckets.length) {
    return (
      <div style={{ padding: '28px 12px', textAlign: 'center', fontSize: 'var(--t-body)', color: 'var(--ink500)' }}>
        Aucun relevé sur cette période.
      </div>
    );
  }

  // Tant que le conteneur n'est pas mesuré, on réserve la hauteur sans rien dessiner : tracer dans une
  // largeur de 0 produirait un instant de graphe écrasé, qu'on lirait comme une donnée.
  if (width <= 0) return <div ref={boite} style={{ height: H }} />;

  const { top, x, y, t0, span, utile } = vue;
  const bucketMs = bucketSeconds * 1000;
  const segs = segments(buckets, bucketMs);
  const trous = trousDeMesure(buckets, bucketMs);
  const surPlusieursJours = vue.t1 - t0 > 36 * 3600_000;

  // Graduations : des REPÈRES d'échelle (ceux des jauges, 100 · 300 · 500), pas les paliers d'état — qui sont les zones.
  const paliers = [100, 300, 500].filter((p) => p < top).concat(top);
  // Repères de temps : 5 au plus, sinon les libellés se chevauchent.
  const pas = Math.max(1, Math.ceil(buckets.length / 5));
  const marques = buckets.filter((_, i) => i % pas === 0);

  // Dernier point RÉELLEMENT MESURÉ (pas le dernier seau : s'il est tombé, il n'a pas de médiane).
  const dernierSeg = segs[segs.length - 1];
  const dernier = dernierSeg?.[dernierSeg.length - 1] ?? null;

  /** Seau sous le curseur. Au-delà de 2 seaux d'écart on est dans un trou : on n'invente aucune lecture. */
  const viser = (e: React.MouseEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const vx = ((e.clientX - r.left) / r.width) * width;
    const t = t0 + ((vx - PAD_L) / utile) * span;
    let proche: HistoryBucket | null = null;
    let variance = Infinity;
    for (const b of buckets) {
      const d = Math.abs(b.t - t);
      if (d < variance) { variance = d; proche = b; }
    }
    setSurvol(proche && variance <= 2 * bucketMs ? proche : null);
  };

  // Lignes de l'encadré. Un seau entièrement tombé ne rend AUCUNE latence : il le dit, il n'affiche pas 0.
  const lines: { txt: string; fort?: boolean; alerte?: boolean }[] = survol
    ? survol.median == null
      ? [
        { txt: heure(survol.t) + (surPlusieursJours ? ` · ${day(survol.t)}` : ''), fort: true },
        { txt: 'service injoignable', alerte: true },
        { txt: `${survol.failures} relevé${survol.failures > 1 ? 's' : ''} sans réponse`, alerte: true },
      ]
      : [
        { txt: heure(survol.t) + (surPlusieursJours ? ` · ${day(survol.t)}` : ''), fort: true },
        { txt: `médiane ${survol.median} ms` },
        { txt: `min ${survol.min} · max ${survol.max} ms` },
        ...(survol.failures > 0 ? [{ txt: `${survol.failures} relevé${survol.failures > 1 ? 's' : ''} sans réponse`, alerte: true }] : []),
      ]
    : [];
  const boiteH = 14 * lines.length + 12;
  const boiteX = survol ? (x(survol.t) > width * 0.62 ? x(survol.t) - 158 : x(survol.t) + 10) : 0;
  const boiteY = survol
    ? Math.min(Math.max(PAD_T, (survol.median != null ? y(survol.median) : PAD_T) - boiteH / 2), H - PAD_B - boiteH)
    : 0;

  return (
    <div ref={boite}>
    <svg
      width={width} height={H} viewBox={`0 0 ${width} ${H}`} role="img" aria-label="Historique de latence"
      style={{ display: 'block', cursor: 'crosshair' }}
      onMouseMove={viser}
      onMouseLeave={() => setSurvol(null)}
    >
      <defs>
        {/* Hachures du TROU : gris et rayé, jamais rouge — le rouge dit une panne MESURÉE, le trou dit
            qu'on n'a rien observé. Confondre les deux inventerait un incident. */}
        <pattern id="histgap" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke="var(--ink400)" strokeWidth="1.4" />
        </pattern>
      </defs>

      {/* 1 · Zones de gravité — repère, pas décor. ⚠️ Une zone dont la borne basse dépasse le plafond
          n'est pas rendue : l'échelle s'adapte au pire relevé, donc un service sain n'affiche QUE le
          vert. C'est une information juste, pas une zone manquante. */}
      {latencyZones(measure).filter((z) => z.de < top).map((z) => {
        const haut = Math.min(z.a, top);
        return <rect key={z.de} x={PAD_L} y={y(haut)} width={utile} height={y(z.de) - y(haut)} fill={z.fill} opacity={OPACITE_ZONE} />;
      })}

      {/* 2 · Trous de mesure */}
      {trous.map(({ a, b }) => {
        const x0 = x(a.t) + vue.largeurSeau;
        return (
          <rect key={`trou-${a.t}`} x={x0} y={PAD_T} width={Math.max(2, x(b.t) - x0)} height={H - PAD_T - PAD_B} fill="url(#histgap)" opacity={0.25}>
            <title>{`${heure(a.t)} → ${heure(b.t)} — trou de mesure : la sonde ne relevait pas (rien n’a été observé)`}</title>
          </rect>
        );
      })}

      {/* 3 · Pannes MESURÉES — sous la courbe : un seau contenant des relevés sans réponse porte une
          bande rouge pleine hauteur. La courbe ne peut rien en dire, il n'y a pas eu de latence. */}
      {buckets.filter((b) => b.failures > 0).map((b) => (
        <rect
          key={`ko-${b.t}`}
          x={x(b.t)} y={PAD_T} width={Math.max(1.5, vue.largeurSeau)} height={H - PAD_T - PAD_B}
          fill="var(--chart-da)" opacity={b.median == null ? 0.28 : 0.16}
        >
          <title>{`${heure(b.t)} — ${b.failures} relevé(s) sans réponse sur ${b.samples}`}</title>
        </rect>
      ))}

      {/* 4 · Grille + échelle */}
      {paliers.map((p) => (
        <g key={p}>
          <line x1={PAD_L} x2={width - 8} y1={y(p)} y2={y(p)} stroke="var(--ink200)" strokeWidth={1} />
          <text x={PAD_L - 7} y={y(p) + 3.5} textAnchor="end" fontSize={9.5} fill="var(--ink400)" className="mono">{p}</text>
        </g>
      ))}

      {/* 5 · Bande min–max : l'amplitude réelle du seau. C'est elle qui empêche un pic de disparaître. */}
      {segs.map((seg, i) => (
        <path
          key={`bande-${i}`}
          d={
            seg.map((b, j) => `${j === 0 ? 'M' : 'L'}${x(b.t)},${y(b.max ?? 0)}`).join('')
            + seg.slice().reverse().map((b) => `L${x(b.t)},${y(b.min ?? 0)}`).join('')
            + 'Z'
          }
          fill="var(--chart-in)"
          opacity={0.22}
        />
      ))}

      {/* 6 · Médiane : la tendance, celle qui décide aussi la couleur des tuiles. */}
      {segs.map((seg, i) => (
        <path
          key={`med-${i}`}
          d={seg.map((b, j) => `${j === 0 ? 'M' : 'L'}${x(b.t)},${y(b.median!)}`).join('')}
          fill="none" stroke="var(--chart-in)" strokeWidth={1.8} strokeLinejoin="round" strokeLinecap="round"
        />
      ))}

      {/* 7 · Axe du temps */}
      {marques.map((b) => (
        <text key={`t-${b.t}`} x={x(b.t)} y={H - 6} textAnchor="middle" fontSize={9} fill="var(--ink400)" className="mono">
          {surPlusieursJours ? day(b.t) : heure(b.t)}
        </text>
      ))}

      {/* 8 · Dernier relevé mesuré — la valeur qu'on vient lire en premier. */}
      {dernier?.median != null && (
        <g>
          <circle cx={x(dernier.t)} cy={y(dernier.median)} r={3.2} fill="var(--chart-in)" stroke="var(--paper)" strokeWidth={1.5} />
          <text
            x={Math.min(x(dernier.t), width - 46)}
            y={Math.max(PAD_T + 8, y(dernier.median) - 8)}
            textAnchor="end" fontSize={10} fontWeight={700} fill="var(--chart-in)" className="mono"
          >
            {dernier.median} ms
          </text>
        </g>
      )}

      {/* 9 · Survol : réticule + lecture du seau, au-dessus de tout. */}
      {survol && (
        <g>
          <line x1={x(survol.t)} x2={x(survol.t)} y1={PAD_T} y2={H - PAD_B} stroke="var(--ink400)" strokeWidth={1} strokeDasharray="3 3" />
          {survol.median != null && (
            <circle cx={x(survol.t)} cy={y(survol.median)} r={3.4} fill="var(--paper)" stroke="var(--chart-in)" strokeWidth={2} />
          )}
          {/* ⚠️ `--elev` n'est pas émis par le thème de la Console (il l'est dans la maquette) : sans
              repli, l'encadré serait transparent et son texte illisible par-dessus la courbe. */}
          <rect x={boiteX} y={boiteY} width={148} height={boiteH} rx={7} fill="var(--elev, var(--paper))" stroke="var(--ink200)" opacity={0.97} />
          {lines.map((l, i) => (
            <text
              key={i}
              x={boiteX + 10} y={boiteY + 18 + i * 14}
              fontSize={9.5} fontWeight={l.fort ? 700 : 500}
              fill={l.alerte ? 'var(--da-fg)' : l.fort ? 'var(--ink900)' : 'var(--ink600)'}
              className="mono"
            >
              {l.txt}
            </text>
          ))}
        </g>
      )}
    </svg>
    </div>
  );
}
